import React, { useState } from 'react';
import expensesApi from '../api/expenses';
import LoadingOverlay from './LoadingOverlay';

interface ExpenseInputProps {
  onSaved?: () => void;
}

const ExpenseInput: React.FC<ExpenseInputProps> = ({ onSaved }) => {
  const [text, setText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;

    setIsLoading(true);
    setMessage('');

    try {
      const parsed = await expensesApi.previewText(text);
      // Save directly without preview
      await expensesApi.create(parsed);
      setText('');
      setMessage(parsed.type === 'income' ? 'Доход успешно добавлен!' : 'Расход успешно добавлен!');
      onSaved?.();
    } catch (error: any) {
      console.error('Error parsing expense:', error);
      const payload = error.response?.data;
      const apiMessage = Array.isArray(payload?.message) ? payload.message.join('; ') : payload?.message;
      const fallback = error.message || 'Не удалось распознать операцию';
      setMessage(apiMessage || fallback);
    } finally {
      setIsLoading(false);
    }
  };

  const exampleTexts = [
    '12 февраля 2026 оплачена аренда офиса 2500 EUR',
    'Заплатил за рекламу в Google 5.02.2026 на сумму 430 USD',
    '03.03.2026 закупка канцелярии для офиса 1200 PLN (не оплачено)',
    '15.03.2026 получили оплату от ООО Рассвет за поставку стройматериалов 5000 BYN',
  ];

  const handleExampleClick = (example: string) => {
    setText(example);
    setMessage('');
  };

  return (
    <div className="relative">
      <LoadingOverlay isVisible={isLoading} />
      <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Опишите расход или доход в свободной форме
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Например: 12 февраля 2026 оплата аренды офиса 2500 EUR не оплачено"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || !text.trim()}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Распознавание...' : 'Распознать'}
            </button>
          </form>

          {message && (
            <p className={`mt-3 text-sm ${message.includes('успешно') ? 'text-green-600' : 'text-red-600'}`}>
              {message}
            </p>
          )}

          <div className="mt-4">
            <p className="text-sm text-gray-500 mb-2">Примеры:</p>
            <div className="flex flex-col gap-2">
              {exampleTexts.map((example, index) => (
                <button
                  key={index}
                  onClick={() => handleExampleClick(example)}
                  className="w-full text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded transition-colors text-left"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
    </div>
  );
};

export default ExpenseInput;
